import { useEffect, useState } from "react";
import { Button, EmptyState, Icon, Notice, Skeleton, StatusBadge, TextField } from "../ui";
import { ItemRow } from "../ui/ItemRow";
import { useSuche, type SuchTreffer } from "./sonderfaelle-api";
import { ApiFehler } from "../api/client";

const ZULETZT_KEY = "rl.kasse.zuletzt-regale";

/** Artikel ohne Etikett: nach Regal (Ziffernblock), Mieter oder Bezeichnung suchen und in den Warenkorb legen. */
export function OhneEtikett({ onWahl, onZurueck, status = "im_regal", titel = "Artikel ohne Etikett", aktionText = "In den Warenkorb" }: { onWahl: (t: SuchTreffer) => Promise<void> | void; onZurueck: () => void; status?: "im_regal" | "verkauft"; titel?: string; aktionText?: string }) {
  const [art, setArt] = useState<"regal" | "mieter" | "bezeichnung">("regal");
  const [q, setQ] = useState("");
  const [regal, setRegal] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState<string | null>(null);
  const [zuletzt, setZuletzt] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(ZULETZT_KEY) ?? "[]") as string[];
    } catch {
      return [];
    }
  });
  const suchbegriff = art === "regal" ? regal : q;
  const suche = useSuche(suchbegriff, art, status);

  useEffect(() => {
    if (art === "regal" && regal.length === 3) {
      const neu = [regal, ...zuletzt.filter((r) => r !== regal)].slice(0, 6);
      setZuletzt(neu);
      try {
        localStorage.setItem(ZULETZT_KEY, JSON.stringify(neu));
      } catch {
        // egal
      }
    }
  }, [regal, art]);

  async function waehle(t: SuchTreffer) {
    setFehler(null);
    setLaeuft(t.id);
    try {
      await onWahl(t);
    } catch (err) {
      setFehler(err instanceof ApiFehler ? err.message : "Das hat nicht geklappt.");
    } finally {
      setLaeuft(null);
    }
  }

  const treffer = suche.data?.treffer ?? [];
  return (
    <div className="sc-kasse">
      <section className="sc-kasse__cart kasse-flow">
        <div className="sc-row">
          <Button variant="quiet" icon="chevron-left" onClick={onZurueck} aria-label="Zurück zum Warenkorb" />
          <h1 className="title">{titel}</h1>
        </div>
        <div className="sc-toolbar" role="radiogroup" aria-label="Suchen in">
          {(["regal", "mieter", "bezeichnung"] as const).map((a) => (
            <button key={a} type="button" className="sc-pill" aria-pressed={art === a} onClick={() => setArt(a)}>
              {a === "regal" ? "Regal" : a === "mieter" ? "Mieter" : "Bezeichnung"}
            </button>
          ))}
        </div>
        {art !== "regal" ? <TextField label="Suchen" icon="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={art === "mieter" ? "Name oder Mieternummer" : "Bezeichnung"} autoFocus data-scanner="true" /> : null}
        {fehler ? <Notice tone="error">{fehler}</Notice> : null}
        {!suchbegriff ? (
          <EmptyState compact illustration="empty-shelf" title={art === "regal" ? "Regalnummer eintippen" : "Suchbegriff eingeben"}>
            {art === "regal" ? "Die Nummer steht auf dem Regalschild." : "Mindestens ein Zeichen."}
          </EmptyState>
        ) : suche.isPending ? (
          <Skeleton lines={3} thumb />
        ) : treffer.length === 0 ? (
          <EmptyState compact illustration="empty-shelf" title="Nichts gefunden">
            {status === "verkauft" ? "Kein verkaufter Artikel passt." : "Kein Artikel im Regal passt. Vielleicht ist er schon verkauft oder noch nicht erfasst."}
          </EmptyState>
        ) : (
          <div>
            <p className="sc-muted sc-small">
              {treffer.length} Treffer{art === "regal" ? ` in Regal ${regal.padStart(3, "0")}` : ""}
            </p>
            {treffer.map((t) => (
              <ItemRow
                key={t.id}
                title={t.bezeichnung}
                photo={t.fotoKey ? `/api/laden/dateien/${t.fotoKey}` : undefined}
                meta={`${t.code} · ${t.mieterName}${art !== "regal" ? ` · Regal ${t.regalNummer}` : ""}`}
                price={t.preisCent}
                status={status === "verkauft" ? <StatusBadge status="sold" /> : undefined}
                action={
                  <Button variant="secondary" icon="plus" loading={laeuft === t.id} onClick={() => void waehle(t)}>
                    {aktionText}
                  </Button>
                }
              />
            ))}
          </div>
        )}
      </section>
      <aside className="sc-kasse__side">
        {art === "regal" ? (
          <>
            <h2 className="heading">Regal wählen</h2>
            <p className="sc-muted sc-small">Nummer vom Regalschild eintippen</p>
            <div className="sc-row" style={{ justifyContent: "space-between" }}>
              <span className="total-lg">{regal.padEnd(3, "·")}</span>
              {treffer[0]?.mieterName && regal.length === 3 ? <StatusBadge status="shelf">{treffer[0].mieterName}</StatusBadge> : null}
            </div>
            <div className="rg-pin__grid" style={{ alignSelf: "center" }}>
              {["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "C"].map((t) => (
                <button
                  key={t}
                  type="button"
                  className={["rg-pin__key", (t === "⌫" || t === "C") && "rg-pin__key--fn"].filter(Boolean).join(" ")}
                  aria-label={t === "⌫" ? "Letzte Ziffer löschen" : t === "C" ? "Löschen" : t}
                  onClick={() => setRegal(t === "⌫" ? regal.slice(0, -1) : t === "C" ? "" : regal.length < 3 ? regal + t : regal)}
                >
                  {t === "⌫" ? <Icon name="delete" size={24} /> : t}
                </button>
              ))}
            </div>
            {zuletzt.length ? (
              <div className="sc-col" style={{ gap: 6 }}>
                <span className="sc-muted sc-small">Zuletzt gesucht</span>
                <div className="sc-toolbar">
                  {zuletzt.map((r) => (
                    <button key={r} type="button" className="sc-pill" onClick={() => setRegal(r)}>
                      {r}
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : (
          <p className="kasse-side__hint">Treffer antippen, um den Artikel in den Warenkorb zu legen. Der Preis kommt aus der Erfassung des Mieters.</p>
        )}
      </aside>
    </div>
  );
}
